import moment, { Moment } from 'moment';

import { EmpBusinessUnit, EmpPosition, EmploymentData } from '~/data/employee';
import { EmploymentDetailType } from '~/data/employment/useEmploymentDetails';
import { AllEnums, Enum } from '~/data/enums';
import { OrgTreeData } from '~/data/orgunits/types';
import { ProposalProps, ProposalType } from '~/data/proposals';
import { empPositionNewTypeToLegacy } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data/newTypeToLegacy';
import { ScheduleEmploymentFormValues } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/useScheduleEmployment';
import { emplDetailsNewTypeToLegacy } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/data/newTypeToLegacy';
import { ScheduleEmploymentDetailFormValues } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/useScheduleEmploymentDetail';
import {
  BusinessUnitData,
  PendingScheduleChanges,
  ScheduleForm,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { filterActiveEnums } from '~/utils/helper';

type PropsType = {
  empCurrentData: EmpPosition;
  employmentCurrentData: EmploymentData;
  employmentCurrentDetail: EmploymentDetailType;
  fEmpBusinessUnits: EmpBusinessUnit[];
  orgTree: OrgTreeData[];
  allEnums: AllEnums;
  contractNumber: string | undefined;
  personNumber: string;
  pendingData: PendingScheduleChanges[];
  mainContract: boolean;
  employmentDetailsProposal?: ProposalProps;
  empPositionProposal?: ProposalProps;
  defaultResponsibleHrId?: number;
};

export const mapDefaultData = (props: PropsType): ScheduleForm[] => {
  const {
    empCurrentData,
    employmentCurrentData,
    employmentCurrentDetail,
    fEmpBusinessUnits,
    orgTree,
    allEnums,
    contractNumber,
    personNumber,
    pendingData,
    mainContract,
    defaultResponsibleHrId,
    employmentDetailsProposal,
    empPositionProposal,
  } = props;

  //Current empl data
  const empData =
    empPositionProposal &&
    empPositionProposal.proposalType === ProposalType.POSITION_INTERNAL_PROPOSAL
      ? empPositionNewTypeToLegacy(empPositionProposal)
      : pendingData?.[0]?.empPosition || empCurrentData;
  const employmentData = employmentCurrentData;
  const employmentDetail: EmploymentDetailType =
    employmentDetailsProposal &&
    employmentDetailsProposal.proposalType ===
      ProposalType.EMPLOYMENT_DETAILS_PROPOSAL
      ? emplDetailsNewTypeToLegacy(employmentDetailsProposal)
      : pendingData?.[0]?.employmentDetails || employmentCurrentDetail;
  // end current empl data
  const defaultCC = empData?.fEmpCostCenters?.find(
    (cc) => cc.fEmpCostCenterIsDefault,
  );
  const businessUnits: BusinessUnitData = (orgTree ?? [])
    .slice(1)
    .reduce((acc, bu) => {
      const selectedBU = fEmpBusinessUnits.find(
        (ebu) => ebu.treeId === bu.fTreeId,
      );
      const key = `fEmpBusinessUnit${bu.fTreeId}` as string;
      return {
        ...acc,
        [key]: selectedBU ? selectedBU.unitId.toString() : '-1',
      };
    }, {});

  const employmentStatuses = filterActiveEnums(allEnums['EMPLOYMENT_STATUS']);
  const jobGrades = filterActiveEnums(allEnums['JOB_GRADE']);

  const getPersonnelGroupEnums = (groupNumber: number) => {
    const isValidGroupNumber = groupNumber >= 1 && groupNumber <= 5;
    return isValidGroupNumber
      ? filterActiveEnums(allEnums[`PERSONNEL_GROUP_${groupNumber}`])
      : [];
  };

  const processEmploymentDataCommonValues = (employment: EmploymentData) => {
    const toMomentOrNull = (value?: string): Moment | null =>
      value ? moment(value) : null;
    const toMoment = (value: string | undefined): Moment =>
      value ? moment(value) : moment(new Date());

    return {
      fEmplTerminationDate: toMomentOrNull(employment?.fEmplTerminationDate),
      fEmplTerminationReason: employment?.fEmplTerminationReason || null,
      fEmplDateArrival: toMoment(employment?.fEmplDateArrival),
      fEmplTrialPeriodEndDate: toMomentOrNull(
        employment?.fEmplTrialPeriodEndDate,
      ),
      fEmplDateStart: toMoment(employment?.fEmplDateStart),
      fEmplTrialPeriod: employment?.fEmplTrialPeriod || null,
      fEmplPlannedEndDate: toMomentOrNull(employment?.fEmplPlannedEndDate),
      fEmplLastDateEmployment: toMomentOrNull(
        employment?.fEmplLastDateEmployment,
      ),
      fEmplFixedTermReason: employment?.fEmplFixedTermReason || null,
      fEmplInfo: employment?.fEmplInfo || '',
    };
  };

  const processEmploymentDetailsCommonValues = (
    employmentDetails: EmploymentDetailType,
  ): ScheduleEmploymentDetailFormValues => {
    const toValidEnumCode = (enums: Enum[], value?: string): string =>
      enums.find((el) => el.code === value)?.code || '';

    return {
      fEmplDetailTaxNumber: employmentDetails?.fEmplDetailTaxNumber || '',
      fEmplDetailStartDate: employmentDetails?.fEmplDetailIsPending
        ? moment(employmentDetails.fEmplDetailStartDate)
        : moment(new Date()),
      fEmplDetailCalcHoursBalance:
        employmentDetails?.fEmplDetailCalcHoursBalance ? 'true' : '',
      fEmplDetailVacBalCalcSixDays:
        employmentDetails?.fEmplDetailVacBalCalcSixDays,
      fEmplDetailJobGrade: toValidEnumCode(
        jobGrades,
        employmentDetails?.fEmplDetailJobGrade,
      ),
      fEmplDetailStatus: toValidEnumCode(
        employmentStatuses,
        employmentDetails?.fEmplDetailStatus,
      ),
      fEmplDetailPersonnelGroup1: toValidEnumCode(
        getPersonnelGroupEnums(1),
        employmentDetails?.fEmplDetailPersonnelGroup1,
      ),
      fEmplDetailPersonnelGroup2: toValidEnumCode(
        getPersonnelGroupEnums(2),
        employmentDetails?.fEmplDetailPersonnelGroup2,
      ),
      fEmplDetailPersonnelGroup3: toValidEnumCode(
        getPersonnelGroupEnums(3),
        employmentDetails?.fEmplDetailPersonnelGroup3,
      ),
      fEmplDetailPersonnelGroup4: toValidEnumCode(
        getPersonnelGroupEnums(4),
        employmentDetails?.fEmplDetailPersonnelGroup4,
      ),
      fEmplDetailPersonnelGroup5: toValidEnumCode(
        getPersonnelGroupEnums(5),
        employmentDetails?.fEmplDetailPersonnelGroup5,
      ),
      fEmplId: employmentDetails.fEmplId,
      customData: employmentDetails?.fEmplDetailCustomData || {},
    };
  };

  const processEmploymentPositionCommonValues = (
    employmentPosition: EmpPosition,
  ): Omit<ScheduleEmploymentFormValues, 'fInternalPositionStartDate'> => {
    const {
      fInternalPositionPosition,
      fEmpJobTitle,
      fInternalPositionInfo,
      fInternalPositionUnit,
    } = employmentPosition ?? {};

    const unitPositionData = empData?.fEmpUnitPositions?.reduce(
      (acc, up) => ({
        ...acc,
        [`${up.fEmpUnitPos}-percent`]: up.fEmpUnitPosPercent,
      }),
      {},
    );

    const costCenterData = employmentPosition?.fEmpCostCenters?.reduce(
      (acc, cc) => ({
        ...acc,
        [`${cc.fEmpCostCenter}-cc-percent`]:
          cc.fEmpCostCenterPercent !== undefined
            ? cc.fEmpCostCenterPercent.toString()
            : '',
        [`${cc.fEmpCostCenter}-cc-customData`]: cc.fEmpCostCenterCustomData,
      }),
      {},
    );

    return {
      fEmpLegalEntity: employmentPosition?.fEmpLegalEntity?.toString(),
      fInternalPositionPosition,
      fEmpJobTitle,
      fInternalPositionInfo,
      fInternalPositionUnit,
      fInternalPositionManager:
        employmentPosition?.fInternalPositionManager?.toString(),
      fEmpUnitPositions: employmentPosition?.fEmpUnitPositions?.map(
        (up) => up.fEmpUnitPos,
      ),
      fEmpCostCenter: defaultCC ? defaultCC.fEmpCostCenter : -1,
      ...unitPositionData,
      ...costCenterData,
    };
  };

  const additionalEmployments = (pendingData ?? [])
    .slice(1)
    .map((additionalData) => {
      if (!Object.keys(additionalData).length) {
        return {};
      }

      const additionalEmployment = additionalData?.employment;
      const additionalEmploymentDetails = additionalData?.employmentDetails;
      const additionalEmpPosition = additionalData?.empPosition;

      return {
        empPosition: {
          ...processEmploymentPositionCommonValues(additionalEmpPosition),
          fInternalPositionStartDate:
            additionalEmpPosition.fInternalPositionIsPending
              ? moment(additionalEmpPosition.fInternalPositionStartDate)
              : moment(new Date()),
        },
        employment: processEmploymentDataCommonValues(additionalEmployment),
        employmentDetails: processEmploymentDetailsCommonValues(
          additionalEmploymentDetails,
        ),
      };
    })
    .filter((data) => !!Object.keys(data).length);

  return [
    {
      empPosition: {
        ...processEmploymentPositionCommonValues(empData),
        fInternalPositionStartDate:
          empData?.fInternalPositionIsPending || empPositionProposal
            ? moment(empData?.fInternalPositionStartDate)
            : moment(new Date()),
        fInternalPositionProposalHrId: defaultResponsibleHrId,
      },
      employment: processEmploymentDataCommonValues(employmentData),
      employmentDetails: {
        ...processEmploymentDetailsCommonValues(employmentDetail),
        fEmplDetailProposalHrId: defaultResponsibleHrId,
        customData: employmentDetail?.fEmplDetailCustomData || {},
      },
      fEmpContractNumber: contractNumber || '',
      fEmpPersonNumber: personNumber || '',
      mainContract: mainContract || false,
      ...businessUnits,
      meta: {
        positionProposal: {
          proposalId: empPositionProposal?.proposalId,
          proposalStatus: empPositionProposal?.proposalStatus,
        },
        detailsProposal: {
          proposalId: employmentDetailsProposal?.proposalId,
          proposalStatus: employmentDetailsProposal?.proposalStatus,
        },
      },
    },
    ...additionalEmployments,
  ];
};
